import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import { DataTable, EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import { LoanType } from '../../../models/loan-type';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { TextKey, useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { AdminPage, ContentCard, RowAction, RowActions, apiError, useConfirm } from '../shared/AdminUi';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

interface LoanTypeRow {
    id: number;
    name: string;
    frequency: string;
    utility: string;
    utilityValue: number | null;
    defaultDays: number;
}

const LoanTypeList: React.FC<ShowAlerts> = () => {
    const t = useT();
    const notify = useNotify();
    const [confirm, confirmDialog] = useConfirm();
    setPageTitle('loanTypes.title');
    const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
    const [loading, setLoading] = useState(true);
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/loan-types');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { data } = await axios.get('loan-types');
                setLoanTypes(Array.isArray(data?.data) ? data.data : []);
            } catch (error) {
                notify.error(t('loanTypes.loadError'));
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDelete = async (row: LoanTypeRow) => {
        if (!canDelete) return;
        const ok = await confirm({ title: t('loanTypes.deleteConfirm', { name: row.name }) });
        if (!ok) return;
        try {
            await axios.delete(`loan-types/${row.id}`);
            notify.success(t('loanTypes.deleted'));
            setLoanTypes((prev) => prev.filter((loanType: LoanType) => loanType.id !== row.id));
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                notify.error(t('loanTypes.inUse'));
            } else {
                notify.error(apiError(error, t('loanTypes.saveError')));
            }
        }
    };

    const rows: LoanTypeRow[] = loanTypes.map((loanType: LoanType) => {
        const frequencyId = Number(loanType.loanTypeKind?.id);
        const fixed = Number(loanType.fixedInterest ?? 1) !== 0;
        return {
            id: loanType.id,
            name: loanType.name,
            frequency: [1, 2, 3, 4].includes(frequencyId) ? t(`frequency.${frequencyId}` as TextKey) : loanType.loanTypeKind?.name ?? '',
            utility: fixed ? `${loanType.interestRate} %` : t('loanTypes.perLoan'),
            utilityValue: fixed ? Number(loanType.interestRate) : null,
            defaultDays: Number(loanType.defaultDays),
        };
    });

    const addButton = (
        <PermissionButton
            allowed={canCreate}
            deniedTooltip={t('common.noPermissionCreate')}
            variant="contained"
            startIcon={<AddIcon />}
            component={NavLink}
            to="/admin/loan-types/new"
            data-tour="loan-type-new"
        >
            {t('loanTypes.newTitle')}
        </PermissionButton>
    );

    return (
        <AdminPage>
            <PageHeader title={t('loanTypes.title')} description={t('loanTypes.subtitle')} actions={addButton} />
            <ContentCard>
                <DataTable<LoanTypeRow>
                    rows={rows}
                    loading={loading}
                    getRowId={(r) => r.id}
                    ariaLabel={t('loanTypes.title')}
                    searchable
                    searchPlaceholder={t('loanTypes.searchPlaceholder')}
                    exportFileName={t('loanTypes.exportFileName')}
                    initialSort={{ columnId: 'name', direction: 'asc' }}
                    columns={[
                        { id: 'name', header: t('common.name'), value: (r) => r.name, sortable: true },
                        { id: 'frequency', header: t('loanTypes.frequency'), value: (r) => r.frequency, sortable: true },
                        {
                            id: 'utility',
                            header: t('loanTypes.utility'),
                            value: (r) => r.utility,
                            align: 'right',
                            sortable: true,
                        },
                        { id: 'days', header: t('loanTypes.cloveday'), value: (r) => r.defaultDays, align: 'right', sortable: true, hideBelow: 'sm' },
                    ]}
                    rowActions={(row) => (
                        <RowActions>
                            <RowAction
                                label={`${t('common.edit')} ${row.name}`}
                                icon={<EditOutlinedIcon fontSize="small" />}
                                allowed={canUpdate}
                                deniedTooltip={t('common.noPermissionEdit')}
                                to={`/admin/loan-types/${row.id}/edit`}
                            />
                            <RowAction
                                label={`${t('common.delete')} ${row.name}`}
                                icon={<DeleteOutlineIcon fontSize="small" />}
                                color="error"
                                allowed={canDelete}
                                deniedTooltip={t('common.noPermissionDelete')}
                                onClick={() => handleDelete(row)}
                            />
                        </RowActions>
                    )}
                    emptyState={
                        <EmptyState
                            icon={<RequestQuoteOutlinedIcon />}
                            title={t('loanTypes.emptyTitle')}
                            description={t('loanTypes.empty')}
                            action={canCreate ? addButton : undefined}
                        />
                    }
                />
            </ContentCard>
            {confirmDialog}
        </AdminPage>
    );
};

export default LoanTypeList;
